import axios, { InternalAxiosRequestConfig } from 'axios';
import qs from 'qs';
import i18n from '@/i18n';
import { getEnvVar } from '@/utils/env';
import router from '@/router';
import routerKey from '@/router/routerKey';
import { BASE_URL, HOTEL_URL } from '@/service/constant/BaseApiUrl';
import { logError } from '@/utils/logError';
import { useUserInfo } from '@/modules/user/useUserInfo';
import ApiKey from '@/service/constant/ApiKey';

const environment = getEnvVar('VITE_SITE');

const userInfo = useUserInfo();

async function callTokenApi() {
  try {
    return await userInfo.getToken();
  } catch (error) {
    logError(error as Error, 'callTokenApi - An error occured - Redirect to the landing page');
    router?.push({ name: routerKey.LANDING_PAGE });
    window.localStorage.clear();
    userInfo.resetUserInfo();
  }
}

// https://axios-http.com/docs/interceptors
//
// Add a request interceptor
axios.interceptors.request.use(
  async function (config: InternalAxiosRequestConfig) {
    if (environment !== 'DEV') {
      // A custom property to avoid adding the token
      // for specific calls. See PromosService as example.
      if (!config?.metadata?.notRequiresToken) {
        const token = await callTokenApi();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
      }
    }
    config.headers['Accept-Language'] = i18n.global.locale.value;
    return config;
  },
  function (error: Error) {
    if (axios.isAxiosError(error)) {
      logError(error, error?.config?.url, qs.stringify(error?.config?.params), error?.config?.data);
    } else {
      logError(error, 'Request Interceptor - A not axios error occurred');
    }
    return Promise.reject(error);
  }
);

// Add a response interceptor
axios.interceptors.response.use(
  function (response) {
    return response;
  },
  function (error: Error) {
    if (error?.response && error?.response?.status === 406) {
      userInfo.resetUserInfo();
      delete axios.defaults.headers.common['Authorization'];
      router.push({ name: 'error-page' });
    }

    if (!isErrorFiltered(error)) {
      if (axios.isAxiosError(error)) {
        logError(error, error?.config?.url, qs.stringify(error?.config?.params), error?.config?.data);
      } else {
        logError(error, 'Response Interceptor - A not axios error occured');
      }
    }
    return Promise.reject(error);
  }
);

const isErrorFiltered = (error: Error) => {
  return hotelSearchNotFound(error) || notYetGeneratedPdf(error);
};

export const hotelSearchNotFound = (error: Error) => {
  const urlDynamic = `${HOTEL_URL}${ApiKey.HOTELS}`;
  const regex = new RegExp(`^${urlDynamic}/.*/info$`);
  return isNotFound(error) && isUrlMatched(error, regex);
};

export const notYetGeneratedPdf = (error: Error) => {
  const urlDynamic = `${BASE_URL}${ApiKey.VIEW_QUOTE}`;
  const regex = new RegExp(`^${urlDynamic}signed/pdf/[A-Z]{2}\\d+$`);
  return isNotFound(error) && isUrlMatched(error, regex);
};

const isUrlMatched = (error: Error, regex: RegExp) => {
  return regex.test(error?.config?.url);
};

const isNotFound = (error: Error) => {
  return error?.response?.status == 404;
};

export default axios;
